import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose, graphql, withApollo } from 'react-apollo';
import { withTheme } from 'styled-components';
import getConfig from 'next/config';
import { withRouter } from 'next/router';

import FlexTable, { FlexCell, FlexRow } from '../ui/FlexTable';
import ButtonBrand from '../ui/Button/ButtonBrand.style';
import Icon from '../ui/Icon';

import { Toggle } from '../ui/List/List.style';

import { formatCardAddress } from '../../lib/formatAddress';
import getCampsiteType, { getCampsiteTypeDictionaryKey } from '../../lib/campsiteTypes';
import { ids } from '../../config/campsiteTypes';
import testingAttr from '../../lib/testingAttr';
import { generateDateRange, filterDatesByMonth } from '../../lib/helpers/tours';

import Note from '../Note';
import { dictionaryItem, getDictionaryItem } from '../../hocs/withDictionary';

import GET_CONFIGURATION from '../../config/graphql/getConfiguration';
import PLACE_EVENT_TYPES, { SINGLE_EVENT_TYPE } from '../../config/eventTypes';
import arrowDownSVG from '../../static/images/icons/ArrowDown.svg';

import {
  Description,
  SubHeading,
  Container,
  Featured,
  Footer,
  Header,
  Image,
  Img,
  LeftCol,
  LowAvailability,
  MissingImg,
  Pricing,
  Row,
  ItemTitle,
  RightCol,
  ToursActions,
  Distance,
  MobileDistance,
  DefaultPartyAndNights,
  Body,
} from './ListingItem.style';
import { dataLayerManager } from '../../lib/dataLayer';
import IbePropTypes from '../../IbePropTypes';
import { metersToMilesFormatter } from '../../lib/helpers/distance';
import { Text } from '../ui';
import { campsiteImageThumbKeys, mapAlternativeImages } from '../Picture/pictureHelper';
import Picture from '../Picture';
import GET_SITES_CONFIG from '../../config/graphql/getSitesConfig';
import { Flex } from '../ui/Grid/Row.style';
import ListingRating from './ListingRating';
import products from '../../config/products';
import { partyConfigToPartyString } from '../Guests/guestHelpers';
import updateRouterQuery from '../../lib/updateRouterQuery';
import routes from '../../constants/routes';
import { ListingRatingWrapper } from './ListingRating.style';
import ListingItemPricing from './ListingItemPricing';

class ListingItem extends Component {
  static propTypes = {
    activePin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    address: PropTypes.shape({}),
    client: PropTypes.shape(IbePropTypes.client).isRequired,
    eventType: PropTypes.number,
    offers: PropTypes.arrayOf(PropTypes.shape({})),
    configuration: PropTypes.shape(IbePropTypes.configuration).isRequired,
    sitesConfig: PropTypes.shape(IbePropTypes.sitesConfig).isRequired,
    currentCampsiteId: PropTypes.string,
    hasLowAvailability: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    index: PropTypes.number,
    gallery: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.shape({ 1: PropTypes.shape(IbePropTypes.optimizedImage) }),
    ]),
    name: PropTypes.string.isRequired,
    numberOfReviews: PropTypes.number,
    memberPrice: PropTypes.number,
    onLoadImage: PropTypes.func,
    onResultClick: PropTypes.func.isRequired,
    rating: PropTypes.number,
    features: PropTypes.arrayOf(PropTypes.shape({})),
    topFeatures: PropTypes.arrayOf(PropTypes.shape()),
    type: PropTypes.number,
    usp: PropTypes.string,
    loadingCampsites: PropTypes.bool.isRequired,
    loadingPrices: PropTypes.bool.isRequired,
    standardPrice: PropTypes.number,
    isFeatured: PropTypes.bool.isRequired,
    theme: PropTypes.shape({
      COLOR_WHITE: PropTypes.string,
      COLOR_AMBER_RANGE: PropTypes.string,
    }),
    placeEvent: PropTypes.arrayOf(PropTypes.shape(IbePropTypes.event)),
    isOpen: PropTypes.bool,
    isTours: PropTypes.bool,
    isUkItinerary: PropTypes.bool,
    tour: PropTypes.shape(IbePropTypes.tour),
    showDates: PropTypes.bool,
    departureMonth: PropTypes.string,
    distanceMeters: PropTypes.number,
    router: PropTypes.shape(IbePropTypes.router).isRequired,
    isFixedInfoWindow: PropTypes.bool,
  };

  static defaultProps = {
    activePin: null,
    address: {
      addressLines: [],
    },
    currentCampsiteId: '',
    eventType: PLACE_EVENT_TYPES.TOURING.id,
    hasLowAvailability: false,
    index: 0,
    memberPrice: null,
    numberOfReviews: null,
    onLoadImage: () => { },
    offers: [],
    rating: null,
    standardPrice: 0,
    features: [],
    topFeatures: [],
    theme: {
      COLOR_WHITE: '',
      COLOR_AMBER_RANGE: '',
    },
    type: null,
    usp: '',
    gallery: {},
    placeEvent: [],
    isOpen: true,
    isTours: false,
    isUkItinerary: false,
    tour: {},
    showDates: false,
    departureMonth: '',
    distanceMeters: null,
    isFixedInfoWindow: false,
  };

  constructor(props) {
    super(props);

    const { router, configuration } = props;
    const isOverseas = router.query.isOverseas === 'true';

    const productId = isOverseas
      ? products.CAMC_MEMBER_PRODUCT
      : products.CAMC_UK_PRODUCT;

    const product = configuration.products.find((prod) => prod.productCode === productId);

    const stringifiedParty = partyConfigToPartyString(product.partyConfig);

    const { FeatureFlags } = getConfig().publicRuntimeConfig.ENVIRONMENT;

    const isPartyFeatureFlagOn = !!FeatureFlags.SitecardOneShowParty;

    this.state = {
      expand: false,
      defaultPartyString: stringifiedParty?.toLowerCase() ?? '',
      isPartyFeatureFlagOn,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.loadingCampsites !== nextProps.loadingCampsites) return true;
    if (this.props.loadingPrices !== nextProps.loadingPrices) return true;
    if (this.props.currentCampsiteId !== nextProps.currentCampsiteId) return true;
    if (this.props.memberPrice !== nextProps.memberPrice) return true;
    if (this.props.standardPrice !== nextProps.standardPrice) return true;
    if (this.props.tour?.id !== nextProps.tour?.id) return true;
    if (this.props.activePin !== nextProps.activePin) return true;
    if (this.props.showDates !== nextProps.showDates) return true;
    if (this.state.expand !== nextState.expand) return true;
    if (this.props.distanceMeters !== nextProps.distanceMeters) return true;
    return false;
  }

  handleProductClick(campsite, event) {
    // First we need to check if the user has clicked the reviews section
    // as that is handled separately in handleReviewsClick
    if (event.target.closest('[data-reviews-button]')) return;
    const { isUkItinerary, isTours } = this.props;
    const data = { ...this.props };
    let categoryType = dataLayerManager.category.SITES;
    if (isUkItinerary) {
      categoryType = dataLayerManager.subCategory.UK_ITINERARY;
      data.subCategory = categoryType;
    }
    if (isTours) {
      categoryType = dataLayerManager.subCategory.EUROPEAN_TOURS;
      data.subCategory = categoryType;
    }
    dataLayerManager.pushProductClick(
      categoryType, data, this.props.index,
    );

    this.props.onResultClick(campsite);
  }

  onLoadImage = () => {
    this.props.onLoadImage(this.props.id);
  }

  handleReviewsClick = () => {
    updateRouterQuery(
      routes.sites,
      {
        siteCode: null,
        reviews: true,
        campsiteId: this.props.id,
        activePin: this.props.id,
      },
    );
  };

  renderImage = (galleryItem) => {
    if (!galleryItem) {
      return (
        <MissingImg
          role="img"
          aria-label={this.props.name}
          url="/static/images/missing-image.svg"
          {...testingAttr(`search-results-campsite__listing-item-${this.props.index}__image`)}
        />
      );
    }

    const galleryImage = mapAlternativeImages(
      galleryItem.url,
      galleryItem.alternativeFormats,
      campsiteImageThumbKeys,
    );

    if (!galleryImage) {
      // maintain compatibility with components that don't utilize new optimized image structure
      return <Img
        alt={this.props.name}
        aria-label={this.props.name}
        src={galleryItem || ''}
        onLoad={this.onLoadImage}
        loading="lazy"
        {...testingAttr(`search-results-campsite__listing-item-${this.props.index}__image`)}
      />;
    }

    return (
      <Picture image={galleryImage}>
        <Img
          alt={this.props.name}
          aria-label={this.props.name}
          src={galleryImage.baseUrl || ''}
          onLoad={this.onLoadImage}
          loading="lazy"
          {...testingAttr(`search-results-campsite__listing-item-${this.props.index}__image`)}
        />
      </Picture>
    );
  }

  render() {
    const {
      address, id, hasLowAvailability, name, offers, eventType, type,
      isTours, client, tour, showDates, departureMonth,
      distanceMeters, configuration, sitesConfig, router,
      isFixedInfoWindow, numberOfReviews,
    } = this.props;

    const isOverseas = router.query.isOverseas === 'true';

    const {
      showALongWayWhenGreaterThanDistanceDecimal,
      showOneDecimalPlaceWhenLessThanDistanceDecimal,
    } = configuration?.configurableValues;

    const campsite = {
      address, id, name, offers,
    };

    const loadingCampsitesString = this.props.loadingCampsites.toString();

    const campsiteType = getCampsiteType(
      sitesConfig.campsiteTypes,
      isTours ? ids.OVERSEAS_TOUR : this.props.type,
    );

    const noRatingCampsiteTypes = [
      ids.UK_AFFILIATED_SITES,
      ids.OVERSEAS_TOUR,
    ];

    const noPriceCampsiteTypes = [
      ids.OVERSEAS_TOUR,
    ];

    const priceOnAskingCampsiteTypes = [
      ids.CERTIFICATED_LOCATIONS,
      ids.UK_AFFILIATED_SITES,
    ];

    const showRating =
      numberOfReviews > 0 &&
      this.props.rating !== null &&
      !noRatingCampsiteTypes.includes(campsiteType.id);
    const showPOA = priceOnAskingCampsiteTypes.includes(campsiteType.id);
    const abilityToShowPrice =
      noPriceCampsiteTypes.includes(campsiteType.id) === false ||
      !!tour?.fromPrice ||
      showPOA;

    const hasPrice = !!(this.props.memberPrice || tour?.fromPrice) && this.props.isOpen;

    const toursNightsSuffix = getDictionaryItem(client, 'ListingItem__ToursNightsSuffix__Text');

    const priceSuffix = (!this.state.isPartyFeatureFlagOn || isTours)
      ? getDictionaryItem(client, 'ListingResult__PriceSuffix__Text')
      : '';

    const galleryEventType = type === ids.OVERSEAS_SITE ? SINGLE_EVENT_TYPE.id : eventType;
    const galleryKey = !Array.isArray(this.props.gallery) ? galleryEventType : 0;

    const filteredTourDates = filterDatesByMonth(tour?.departureDates, departureMonth);
    const tourDatesToShow = filteredTourDates.length ? filteredTourDates : tour?.departureDates;

    const isSelected = this.props.activePin?.toString() === this.props.id?.toString();

    const campsiteTypeDictText = getCampsiteTypeDictionaryKey(campsiteType.id);
    const campsiteTypeText = getDictionaryItem(client, `${campsiteTypeDictText}__Text`) ?? campsiteType.text;
    // Regex used to remove the campsite type from the campsite name
    const campsiteTypeRegex = new RegExp(campsiteTypeText, 'gi');
    const campsiteName = this.props.name?.replace(campsiteTypeRegex, '');

    const distanceText = metersToMilesFormatter(
      distanceMeters,
      showALongWayWhenGreaterThanDistanceDecimal,
      showOneDecimalPlaceWhenLessThanDistanceDecimal,
      getDictionaryItem(client, 'ListingResult__DistanceMiles__Text'),
      getDictionaryItem(client, 'ListingResult__DistanceLongWay__Text'),
      true,
    );

    return (
      <Container
        className={isSelected ? 'selected' : ''}
        type={campsiteType.id}
        loading={loadingCampsitesString}
        featured={this.props.isFeatured}
        selected={isSelected}
        selectedColor={campsiteType.color}
        onClick={isTours
          ? undefined
          : (event) => this.handleProductClick(campsite, event)
        }
        {...testingAttr(`search-results-campsite__listing-item-${this.props.index}`)}
      >
        {this.props.isFeatured &&
          <Featured>Featured Site</Featured>
        }
        <Row wrap={isTours ? 'nowrap' : ''}>
          <LeftCol>
            <Image>
              {this.renderImage(this.props.gallery?.[galleryKey])}
            </Image>
            <Flex position="relative" marginTop="auto" justify="space-between">
              {showRating && isFixedInfoWindow &&
                <ListingRating
                  rating={this.props.rating}
                  numberOfReviews={this.props.numberOfReviews}
                  onClick={this.handleReviewsClick}
                />
              }
              {distanceMeters &&
                <MobileDistance>
                  {distanceText}
                </MobileDistance>
              }
            </Flex>
          </LeftCol>
          <RightCol>
            <Flex justify="space-between" position="relative">
              <Note
                backgroundColor={campsiteType.color}
                align="center"
                color={this.props.theme.COLOR_WHITE}
                text={campsiteTypeText}
              />
              {showRating && !isFixedInfoWindow &&
                <ListingRatingWrapper>
                  <ListingRating
                    rating={this.props.rating}
                    numberOfReviews={this.props.numberOfReviews}
                    onClick={this.handleReviewsClick}
                  />
                </ListingRatingWrapper>
              }
            </Flex>
            <Body>
              <Header>
                <ItemTitle {...testingAttr(`search-results-campsite__listing-item-${this.props.index}__title`)}>
                  {campsiteName}
                </ItemTitle>
                {isTours &&
                  <SubHeading>
                    <span>{tour?.numberOfNights} {toursNightsSuffix}</span>
                  </SubHeading>
                }
                <Description
                  isTours={isTours}
                  {...testingAttr(`search-results-campsite__listing-item-${this.props.index}__address`)}
                >
                  {isTours ? tour?.description :
                    formatCardAddress(this.props.address, isOverseas)}
                </Description>
              </Header>
              <Footer>
                {distanceMeters && !isFixedInfoWindow &&
                  <Distance>
                    {distanceText}
                  </Distance>
                }
                {abilityToShowPrice &&
                  <Pricing align={isTours ? 'left' : 'right'}>
                    <ListingItemPricing
                      hasPrice={hasPrice}
                      index={this.props.index}
                      isOpen={this.props.isOpen}
                      isTours={isTours}
                      loadingPrices={this.props.loadingPrices}
                      memberPrice={this.props.memberPrice}
                      priceSuffix={priceSuffix}
                      showPOA={showPOA}
                      tour={tour}
                    />
                  </Pricing>
                }
                {this.state.isPartyFeatureFlagOn && hasPrice && !isTours &&
                  <DefaultPartyAndNights>
                    <Text>{this.state.defaultPartyString}</Text>
                    <Text>|</Text>
                    <Text dictionary={dictionaryItem('ListingItem', 'DefaultNights')} />
                  </DefaultPartyAndNights>
                }
                {hasLowAvailability &&
                  <LowAvailability dictionary={dictionaryItem('ListingItem', 'LowAvailability')} />
                }
              </Footer>
            </Body>
          </RightCol>
        </Row>
        <Row>
          {isTours &&
            <ToursActions>
              <ButtonBrand
                block
                flat
                marginBottom="0.5rem"
                size="small"
                type="button"
                dictionary={dictionaryItem('SearchResults', 'MoreDetails')}
                onClick={(event) => this.handleProductClick(campsite, event)}
              />
              {showDates && tourDatesToShow?.length > 0 && (
                <>
                  <Toggle
                    block
                    bordered
                    onClick={() => this.setState({ expand: !this.state.expand })}
                    size="small"
                  >
                    <span>{getDictionaryItem(
                      client,
                      `SearchResults__${this.state.expand ? 'HideDates' : 'ShowDates'}__Button`,
                    )}
                    </span>
                    <Icon
                      icon={arrowDownSVG}
                      size="0.625rem"
                      style={{ marginLeft: '0.5rem' }}
                    />
                  </Toggle>
                  {this.state.expand &&
                    <FlexTable>
                      {tourDatesToShow
                        .map((departureDate) => (
                          <FlexRow invert key={departureDate}>
                            <FlexCell small>
                              {generateDateRange(departureDate, tour?.numberOfNights)}
                            </FlexCell>
                          </FlexRow>
                        ))}
                    </FlexTable>
                  }
                </>
              )}
            </ToursActions>
          }
        </Row>
      </Container>
    );
  }
}

export default compose(
  withTheme,
  withApollo,
  withRouter,
  graphql(GET_CONFIGURATION, {
    props: ({ data }) => ({
      configuration: data.configuration,
    }),
  }),
  graphql(GET_SITES_CONFIG, {
    props: ({ data }) => ({
      sitesConfig: data.configurationSites,
    }),
  }),
)(ListingItem);
